/*
  NDI Cart Drawer — vanilla Custom Element. No Dawn cart JS.
  - Intercepts every /cart/add form (capture phase) so NDI cards + the Dawn product
    page both open this drawer instead of navigating.
  - Quantity / remove use the Cart AJAX API with the Section Rendering API; only the
    [data-ndi-cart-contents] inner is swapped, so the element + open state persist.
  - Discount "Apply" hands off to Shopify's /discount/{code} (applies through checkout).
*/
(function () {
  var SECTION = 'ndi-cart-drawer';

  class NdiCartDrawer extends HTMLElement {
    connectedCallback() {
      this.overlayLock = false;

      // Delegated controls (survive inner re-render).
      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('change', this._onChange.bind(this));
      this.addEventListener('submit', this._onSubmit.bind(this));

      // Global hooks (bind once).
      if (!NdiCartDrawer._wired) {
        NdiCartDrawer._wired = true;
        // Intercept add-to-cart everywhere, before Dawn's product-form handler.
        document.addEventListener('submit', NdiCartDrawer._onAddCapture, true);
        // Header cart pill opens the drawer.
        document.addEventListener('click', function (e) {
          var pill = e.target.closest('.ndi-pill--cart, [data-ndi-cart-toggle]');
          if (pill) {
            e.preventDefault();
            var d = document.querySelector('ndi-cart-drawer');
            if (d) d.open();
          }
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') {
            var d = document.querySelector('ndi-cart-drawer');
            if (d && d.classList.contains('is-open')) d.close();
          }
        });
      }
      NdiCartDrawer.instance = this;
    }

    open() {
      this.classList.add('is-open');
      document.documentElement.classList.add('ndi-cart-open');
      document.documentElement.style.overflow = 'hidden';
      var btn = this.querySelector('[data-ndi-cart-close]');
      if (btn && btn.focus) {
        try { btn.focus({ preventScroll: true }); } catch (e) { btn.focus(); }
      }
    }

    close() {
      this.classList.remove('is-open');
      document.documentElement.classList.remove('ndi-cart-open');
      document.documentElement.style.overflow = '';
    }

    _onClick(e) {
      if (e.target.closest('[data-ndi-cart-close]')) {
        e.preventDefault();
        this.close();
        return;
      }
      var more = e.target.closest('[data-ndi-cart-payments-more]');
      if (more) {
        more.closest('.ndi-cart-drawer__payments').classList.add('is-expanded');
        return;
      }
      var remove = e.target.closest('[data-ndi-cart-remove]');
      if (remove) {
        this._change(parseInt(remove.getAttribute('data-ndi-cart-remove'), 10), 0);
        return;
      }
      var minus = e.target.closest('[data-ndi-cart-minus]');
      if (minus) {
        var li = parseInt(minus.getAttribute('data-ndi-cart-minus'), 10);
        this._change(li, Math.max(0, this._qty(li) - 1));
        return;
      }
      var plus = e.target.closest('[data-ndi-cart-plus]');
      if (plus) {
        var lp = parseInt(plus.getAttribute('data-ndi-cart-plus'), 10);
        this._change(lp, this._qty(lp) + 1);
      }
    }

    _onChange(e) {
      var input = e.target.closest('[data-ndi-cart-input]');
      if (!input) return;
      var line = parseInt(input.getAttribute('data-ndi-cart-input'), 10);
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 1;
      this._change(line, qty);
    }

    _onSubmit(e) {
      var form = e.target.closest('[data-ndi-cart-discount]');
      if (!form) return;
      e.preventDefault();
      var input = form.querySelector('input[name="discount"]');
      var code = input && input.value.trim();
      if (!code) return;

      // /discount/{code} is a navigation endpoint — it sets the discount cookie and
      // then redirects. Fetching it instead of assigning location keeps the shopper
      // in the drawer: the cookie still gets set (same-origin, credentials included),
      // and we re-render the drawer to show the new totals.
      // `redirect=/cart?view=drawer-noop` keeps the response tiny — we never render it.
      var self = this;
      this._busy(true);
      this._discountError(form, '');
      fetch('/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/cart'), {
        credentials: 'same-origin'
      })
        .then(function () { return self.refresh(); })
        .then(function () {
          // Shopify gives no JSON here, so confirm from the re-rendered markup:
          // an accepted code shows up as a discount row in the drawer.
          var applied = self.querySelector('[data-ndi-cart-discount-applied]');
          if (applied) {
            if (input) input.value = '';
          } else {
            self._discountError(form, 'That code isn’t valid for this cart.');
          }
        })
        .catch(function () {
          self._discountError(form, 'Couldn’t apply that code. Please try again.');
        })
        .finally(function () { self._busy(false); });
    }

    // Inline validation message under the discount field (no alert, no navigation).
    _discountError(form, message) {
      var el = form.querySelector('[data-ndi-cart-discount-error]');
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
    }

    _qty(line) {
      var input = this.querySelector('[data-ndi-cart-input="' + line + '"]');
      return input ? parseInt(input.value, 10) || 0 : 0;
    }

    _change(line, quantity) {
      this._busy(true);
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ line: line, quantity: quantity, sections: SECTION, sections_url: '/' })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var d = NdiCartDrawer.instance;
          if (data.sections && data.sections[SECTION]) { d._swap(data.sections[SECTION]); }
          else { return d.refresh(); }
        })
        .catch(function () {})
        .finally(function () { NdiCartDrawer.instance._busy(false); });
    }

    refresh() {
      var self = this;
      return fetch('/?sections=' + SECTION, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) { if (data[SECTION]) self._swap(data[SECTION]); });
    }

    _swap(html) {
      var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ndi-cart-contents]');
      var current = this.querySelector('[data-ndi-cart-contents]');
      if (fresh && current) current.replaceWith(fresh);
      this._syncBubble();
    }

    _busy(state) {
      this.classList.toggle('is-loading', !!state);
      this.toggleAttribute('aria-busy', !!state);
    }

    _syncBubble() {
      var countEl = this.querySelector('[data-ndi-cart-count]');
      var count = countEl ? parseInt(countEl.textContent, 10) || 0 : 0;
      document.querySelectorAll('.ndi-pill--cart .ndi-pill__bubble').forEach(function (b) {
        b.setAttribute('data-count', count);
        b.textContent = count > 0 ? count : '';
      });
      // Native replacement for Dawn's PUB_SUB_EVENTS.cartUpdate — lets any listener
      // (e.g. the header) react without Dawn's pubsub.js.
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: count } }));
    }
  }

  // Capture-phase add-to-cart interceptor (static so it binds once).
  NdiCartDrawer._onAddCapture = function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    var isAdd = form.matches('[data-ndi-cart-add]') ||
      (form.getAttribute('action') || '').indexOf('/cart/add') !== -1;
    if (!isAdd) return;

    e.preventDefault();
    e.stopImmediatePropagation(); // beat Dawn's <product-form> submit handler

    var drawer = document.querySelector('ndi-cart-drawer');
    var fd = new FormData(form);
    fd.append('sections', SECTION);
    fd.append('sections_url', '/');

    // Open immediately (optimistic) so it feels instant; contents update on response.
    if (drawer) { drawer.open(); drawer._busy(true); }
    fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/javascript' }, body: fd })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      })
      .then(function (data) {
        var d = drawer || document.querySelector('ndi-cart-drawer');
        if (!d) { window.location.href = '/cart'; return; }
        // Add succeeded — refresh the drawer contents (already open).
        if (data.sections && data.sections[SECTION]) { d._swap(data.sections[SECTION]); }
        else { d.refresh(); }
      })
      .catch(function () {
        // On error (e.g. sold out), fall back to the cart page so the user isn't stuck.
        window.location.href = '/cart';
      })
      .finally(function () { if (drawer) drawer._busy(false); });
  };

  if (!customElements.get('ndi-cart-drawer')) {
    customElements.define('ndi-cart-drawer', NdiCartDrawer);
  }
})();
